import { useRef } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '@/components/ui/checkbox'
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { webalize } from '@/lib/catalog/webalize'

import { WarehouseFormValues } from './schema'

interface WarehouseFormProps {
  autoGenerateCode?: boolean
}

export const WarehouseForm = ({ autoGenerateCode = false }: WarehouseFormProps) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'warehouse.warehouses',
  })
  const { control, setValue } = useFormContext<WarehouseFormValues>()
  const codeManuallyEdited = useRef(false)

  const handleNameChange = (name: string) => {
    if (!autoGenerateCode || codeManuallyEdited.current) {
      return
    }

    setValue('code', webalize(name), {
      shouldValidate: Boolean(name),
    })
  }

  return (
    <div className="space-y-4">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.name.label')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('form.name.placeholder')}
                {...field}
                autoComplete="off"
                onChange={(event) => {
                  field.onChange(event)
                  handleNameChange(event.target.value)
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="code"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('form.code.label')}</FormLabel>
            <FormControl>
              <Input
                placeholder={t('form.code.placeholder')}
                {...field}
                autoComplete="off"
                onChange={(event) => {
                  codeManuallyEdited.current = true
                  field.onChange(event)
                }}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="isActive"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value ?? true}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t('form.isActive.label')}</FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="isDefault"
        render={({ field }) => (
          <FormItem className="flex flex-row items-start space-x-3 space-y-0">
            <FormControl>
              <Checkbox
                checked={field.value ?? false}
                onCheckedChange={field.onChange}
              />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel>{t('form.isDefault.label')}</FormLabel>
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  )
}
